import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { enumToArray } from '@digilize/shared/utils/helpers';
import {
  ApiDataResponse,
  AuctionBid,
  AuctionMessage,
  Category,
  DataApiResponseLegacy,
  Event,
  ExternalFeedProduct,
  MarketplaceAuction,
  Page,
  PageType,
  Product,
  ProductRequest,
  ProductType,
  Shop,
  ShopTaxModel,
  ShopTaxUpdateModel,
  Tag,
  Tax,
} from '@shared/definitions';

@Injectable({
  providedIn: 'root',
})
export class ShopApiService {
  readonly shopBaseUrl = '/api/v1.0/shops';
  constructor(private http: HttpClient) {}

  // Category

  getCategories = () => this.http.get<Category[]>('/api/v1.0/categories');

  // Shop

  getShops = (params?) => this.http.get<DataApiResponseLegacy<Shop[]>>(this.shopBaseUrl, { params: params });

  // Every shop of the caller's tenant, as a plain array. A signed-in caller is
  // scoped by their token; an anonymous one has to name the tenant itself
  // (shop_id / company_id / company_group_id / point_of_sale_id) or the request
  // fails with shop_context_required.
  // `context` is for callers whose failure must stay invisible — pauly-auth's BrandingService passes
  // SKIP_ERROR_INTERCEPTOR so a stale shop id cannot pop an error snackbar over the portal. Same
  // shape as AuthApiService.authWithCode; omitting it keeps the interceptor's default behaviour.
  getAllShops = (params?, context?: HttpContext) =>
    this.http.get<Shop[]>(`${this.shopBaseUrl}/all`, { params: params, ...(context ? { context } : {}) });

  // The shop the request itself resolves to, for a caller that holds no id at all — the portal
  // opened from a cinema's own website. Anonymous, and returns one Shop, exactly what
  // `GET /shops/{id}` returns. The backend tries, in order: an explicit shop_id / point_of_sale_id,
  // then the request's domain (the browser's Origin header, which is the rung production takes),
  // then the caller's tenant, then the platform's oldest shop — every signal is a hint, so an
  // unknown one falls through instead of failing. Use this instead of `getAllShops()[0]`, which
  // was never a shop-resolution mechanism and reorders as soon as a shop is added or renamed.
  getCurrentShop = (params?, context?: HttpContext) =>
    this.http.get<Shop>(`${this.shopBaseUrl}/current`, { params: params, ...(context ? { context } : {}) });

  getShop = (shopId: string, context?: HttpContext) => {
    return this.http.get<Shop>(`${this.shopBaseUrl}/${shopId}`, { ...(context ? { context } : {}) });
  };
  getShopPublic = (id: string) => this.http.get<Shop>(`${this.shopBaseUrl}/${id}/public`);

  getShopByPermalink = (permalink: string) => this.http.get<Shop>(`${this.shopBaseUrl}/${permalink}`);

  getShopsByCategoryPermalink = (permalink: string, params?) =>
    this.http.get(`/api/v1.0/categories/${permalink}/shops`, { params });

  addShop = (shop: any) => this.http.post<Shop>(this.shopBaseUrl, shop);

  updateShop = (shop: any) => this.http.put<Shop>(this.shopBaseUrl, shop);

  setActiveShop = (id: string, active: boolean) =>
    this.http.put(`${this.shopBaseUrl}/activate`, { id: id, active: active });

  checkPaymentToken = (token: string) =>
    this.http.get(`${this.shopBaseUrl}/payment-methods?token`, { params: { key: token } });

  validateShops = (shopIds: string[]) => this.http.post<any>('${this.shopBaseUrl}/validate', { ids: shopIds });

  getEvent(shopId: string) {
    return this.http.get<Event>(`${this.shopBaseUrl}/${shopId}/event`);
  }

  getEventByShopPermalink(permalink: string) {
    return this.http.get<Event>(`${this.shopBaseUrl}/${permalink}/event`);
  }

  // getPaymentMethods = (shopId: string) =>
  //   this.http.get(`${this.shopBaseUrl}/payment-methods`, { params: { key: token } });

  // Product

  getProducts = (params?) => this.http.get<DataApiResponseLegacy<Product[]>>('/api/v1.0/products', { params: params });

  getProductsWithNoTickets = (params?) => {
    const productTypes = enumToArray(ProductType, [ProductType.Ticket]).map((i) => i.value);
    const queryParams = { ...params, product_types: productTypes };
    return this.getProducts(queryParams);
  };

  getLikedProducts = (params?) =>
    this.http.get<DataApiResponseLegacy<Product[]>>('/api/v1.0/products/liked', { params: params });

  getProductsByShopPermalink = (permalink, withDescription = false) => {
    const params = withDescription ? { include: 'description' } : {};
    return this.http.get<DataApiResponseLegacy<Product[]>>(`${this.shopBaseUrl}/${permalink}/products`, {
      params: params,
    });
  };

  getProductsByGroupPermalink = (permalink) =>
    this.http.get<DataApiResponseLegacy<Product[]>>(`/api/v1.0/groups/${permalink}/products`);

  getHighlightedProducts = () => this.http.get<Product[]>('/api/v1.0/products/highlighted');

  getVoucherProducts = () => this.http.get<DataApiResponseLegacy<Product>>('/api/v1.0/products/by-vouchers');

  getProduct = (id: string) => this.http.get(`/api/v1.0/products/${id}`);

  getProductPublic = (id: string) => this.http.get<Product>(`/api/v1.0/products/${id}/public`);

  getProductByShopPermalink = (permalink: string, id: string) =>
    this.http.get(`${this.shopBaseUrl}/${permalink}/products/${id}`);

  addProduct = (product: any) => this.http.post('/api/v1.0/products', product);

  updateProduct = (product: any) => this.http.put('/api/v1.0/products', product);

  setActiveProduct = (id: string, active: boolean) =>
    this.http.put('/api/v1.0/products/activate', { id: id, active: active });

  getProductMakers = (key: string) => this.http.get<string[]>(`/api/v1.0/products/makers?search=${key}`);

  sendRequestInfo = (productRequest: ProductRequest) => this.http.post('/api/v1.0/forms/info', productRequest);

  addProductLike = (id: string) => this.http.post(`/api/v1.0/products/${id}/like`, { id });

  removeProductLike = (id: string) => this.http.delete(`/api/v1.0/products/${id}/like`);

  getFeedProducts = (params) =>
    this.http.get<ApiDataResponse<ExternalFeedProduct[]>>(`/api/v1.0/product-feeds/entries/search`, { params });

  exportProducts = (companyId: string) =>
    this.http.get(`/api/v1.0/products/export?company=${companyId}`, { responseType: 'blob' });

  importProducts = (form: FormData, headers?: any) =>
    this.http.post(`/api/v1.0/products/import`, form, { headers: headers });
  getPublicUpsellProducts = (id: string) =>
    this.http.get<DataApiResponseLegacy<Product[]>>(`/api/v1.0/products/${id}/upsell`);

  // Tags

  addTag = (body) => this.http.post(`/api/v1.0/product-pricing-tags`, body);

  editTag = (body) => this.http.put(`/api/v1.0/product-pricing-tags`, body);

  getTags = (params?) => this.http.get<DataApiResponseLegacy<Tag[]>>(`/api/v1.0/product-pricing-tags`, { params });

  getTag = (tagId: string) => this.http.get<Tag>(`/api/v1.0/product-pricing-tags/${tagId}`);

  removeTag = (id: string) => this.http.delete(`/api/v1.0/product-pricing-tags/${id}`);

  // Events

  getEventsByShopPermalink = (permalink: string) =>
    this.http.get<DataApiResponseLegacy<Event[]>>(`${this.shopBaseUrl}/${permalink}/events`);

  // Taxes

  getTaxes = () => this.http.get<Tax[]>('/api/v1.0/taxes');

  // PAUL-3671: the shop's Balance VAT account per rate. Deliberately not part of the shop PUT, which
  // replaces child lists wholesale — see ShopTaxModel.
  getShopTaxes = (shopId: string) => this.http.get<ShopTaxModel[]>(`${this.shopBaseUrl}/${shopId}/taxes`);

  updateShopTaxes = (shopId: string, rows: ShopTaxUpdateModel[]) =>
    this.http.put<ShopTaxModel[]>(`${this.shopBaseUrl}/${shopId}/taxes`, rows);

  // Search

  search = (term: string) => this.http.get(`/api/v1.0/search?term=${term}`);

  searchProducts = (term: string, params?) =>
    this.http.get(`/api/v1.0/products/search?term=${term}`, { params: params });

  searchShops = (term: string, params?) => this.http.get(`${this.shopBaseUrl}/search?term=${term}`, { params: params });

  // Pages

  connectShopToPage = (shopId: string, page: Page, pageType: PageType) =>
    this.http.put(`${this.shopBaseUrl}/${shopId}/${pageType}`, page);

  searchAuctions = (term: string, params?) =>
    this.http.get(`/api/v1.0/auctions/search?term=${term}`, { params: params });

  // Marketplace

  getAuctions = (params?) => this.http.get('/api/v1.0/auctions', { params: params });

  getLikedAuctions = (params?) => this.http.get('/api/v1.0/auctions/liked', { params: params });

  getOwnedAuctions = (params?) => this.http.get('/api/v1.0/auctions/owned', { params: params });

  getAuction = (id: string) => this.http.get(`/api/v1.0/auctions/${id}`);

  addAuction = (auctionRequest: MarketplaceAuction) => this.http.post('/api/v1.0/auctions', auctionRequest);

  editAuction = (auctionRequest: MarketplaceAuction) => this.http.put('/api/v1.0/auctions', auctionRequest);

  addAuctionLike = (id: string) => this.http.post(`/api/v1.0/auctions/${id}/like`, { id });

  removeAuctionLike = (id: string) => this.http.delete(`/api/v1.0/auctions/${id}/like`);

  getAuctionBids = (id: string) => this.http.get(`/api/v1.0/auctions/${id}/bids`);

  addAuctionBid = (id: string, bidRequest: AuctionBid) => this.http.post(`/api/v1.0/auctions/${id}/bids`, bidRequest);

  removeAuctionBid = (id: string) => this.http.delete(`/api/v1.0/auctions/bids/${id}/remove`);

  declineAuctionBid = (id: string) => this.http.delete(`/api/v1.0/auctions/bids/${id}/decline`);

  sendAuctionMessage = (id: string, messageRequest: AuctionMessage) =>
    this.http.post(`/api/v1.0/auctions/${id}/messages`, messageRequest);

  setAuctionStatus = (id: string, status: number) =>
    this.http.put(`/api/v1.0/auctions/${id}/status/${status}`, { status });

  deleteAuction = (id: string) => this.http.delete(`/api/v1.0/auctions/${id}`);
}
